"use client"

import { useState, useEffect } from "react"
import { Link, useNavigate } from "react-router-dom"
import { ref, onValue } from "../lib/firebase-database"
import { db, auth } from "../firebase"
import { ArrowLeft, Search, Navigation, XIcon as XMarkIcon, ChevronDownIcon } from "lucide-react"
import { MobileBottomNav } from "../components/MobileBottomNav"
import { useAuth } from "../hooks/useAuth"
import AdCard from "./AdCard"
import Footer from "../components/Footer"
import LoginPopup from "./login-popup"
import LogoutConfirmationPopup from "./LogoutConfirmationPopup"
import AvatarCircle from "./AvatarCircle"
import OptimizedImage from "./OptimizedImage"

const UserOffersPage = () => {
  const [offers, setOffers] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  const [searchQuery, setSearchQuery] = useState("")
  const [userLocation, setUserLocation] = useState(null)
  const [showTnC, setShowTnC] = useState(false)
  const [selectedTnC, setSelectedTnC] = useState("")
  const [isNearbySorted, setIsNearbySorted] = useState(false)
  const [showMobileMenu, setShowMobileMenu] = useState(false)
  const [isMenuClosing, setIsMenuClosing] = useState(false)
  const [showProfileMenu, setShowProfileMenu] = useState(false)
  const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false)
  const [isBlurred, setIsBlurred] = useState(false)
  const [showLoginPopup, setShowLoginPopup] = useState(false)
  const navigate = useNavigate()
  const { user, isAdmin, isOwner } = useAuth()

  useEffect(() => {
    const handleResize = () => {
      const newIsMobile = window.innerWidth < 768
      setIsMobile(newIsMobile)
      if (!newIsMobile) {
        setShowMobileMenu(false)
        document.body.style.overflow = "auto"
      }
    }
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  useEffect(() => {
    // Get user's location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          })
        },
        (error) => console.error("Error getting location:", error),
      )
    }

    const villasRef = ref(db, "villas")
    onValue(villasRef, (snapshot) => {
      const data = snapshot.val()
      if (data) {
        const activeOffers = Object.entries(data)
          .map(([key, villa]) => ({
            _id: key,
            ...villa,
          }))
          .filter((villa) => villa.isFeaturedOffer && villa.discount > 0 && new Date(villa.offerValidTill) > new Date())
        setOffers(activeOffers)
      }
      setIsLoading(false)
    })
  }, [])

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371 // Radius of the earth in km
    const dLat = (lat2 - lat1) * (Math.PI / 180)
    const dLon = (lon2 - lon1) * (Math.PI / 180)
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) * Math.sin(dLon / 2) * Math.sin(dLon / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    return R * c // Distance in km
  }

  const filterProperties = () => {
    if (searchQuery.length > 0) {
      const searchTerms = searchQuery.toLowerCase().split(" ")
      return offers.filter((property) => {
        const propertyText = `${property.name || ""} ${property.mainCity || ""} ${property.address || ""}`.toLowerCase()
        return searchTerms.every((term) => propertyText.includes(term))
      })
    }
    return offers
  }

  const handleNearbySort = () => {
    if (!userLocation) return

    setIsNearbySorted(!isNearbySorted)
    const sortedOffers = [...offers]
      .map((property) => ({
        ...property,
        distance: property.location
          ? calculateDistance(userLocation.lat, userLocation.lng, property.location.lat, property.location.lng)
          : Number.POSITIVE_INFINITY,
      }))
      .sort((a, b) => (isNearbySorted ? b.distance - a.distance : a.distance - b.distance))

    setOffers(sortedOffers)
  }

  const toggleMobileMenu = () => {
    if (showMobileMenu) {
      setIsMenuClosing(true)
      setTimeout(() => {
        setShowMobileMenu(false)
        setIsMenuClosing(false)
        setIsBlurred(false)
        document.body.style.overflow = "auto"
      }, 300)
    } else {
      setShowMobileMenu(true)
      setIsBlurred(true)
      document.body.style.overflow = "hidden"
    }
  }

  const handleLogoutClick = () => {
    setShowLogoutConfirmation(true)
    setShowProfileMenu(false)
    if (isMobile) toggleMobileMenu()
  }

  const handleLogoutConfirm = () => {
    auth.signOut()
    setShowLogoutConfirmation(false)
    navigate("/")
  }

  const handleLogoutCancel = () => setShowLogoutConfirmation(false)

  const goToProfile = () => {
    navigate("/profile")
    setShowProfileMenu(false)
    if (isMobile) toggleMobileMenu()
  }

  const goToAdminDashboard = () => {
    navigate("/admin")
    setShowProfileMenu(false)
    if (isMobile) toggleMobileMenu()
  }

  const toggleLoginPopup = (show) => {
    setShowLoginPopup(show)
    setIsBlurred(show)
    document.body.style.overflow = show ? "hidden" : "auto"
  }

  const filteredOffers = filterProperties()

  const TermsModal = ({ isOpen, onClose, terms }) => {
    if (!isOpen) return null

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg max-w-md w-full max-h-[80vh] overflow-y-auto">
          <div className="p-4 border-b">
            <h3 className="text-lg font-semibold">Terms & Conditions</h3>
          </div>
          <div className="p-4">
            <p className="text-gray-600">
              {terms ||
                "Offer valid for new bookings only. Subject to availability. Cannot be combined with other offers."}
            </p>
          </div>
          <div className="p-4 border-t">
            <button onClick={onClose} className="w-full bg-black text-white py-2 rounded-lg hover:bg-gray-800">
              Close
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#EDEFEF]">
      {/* Header for desktop */}
      {!isMobile && (
        <header className="bg-black text-white p-4 animate-fadeIn z-50">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="flex justify-between items-center">
              <h1 className="text-2xl font-bold">VillaKosh</h1>
              <nav className="flex items-center space-x-4">
                <Link
                  to="/for-owner"
                  className="hover:bg-gray-700 px-3 py-2 rounded transition duration-300 ease-in-out transform hover:scale-105"
                >
                  For Owners
                </Link>
                {isAdmin && (
                  <Link
                    to="/admin"
                    className="hover:bg-gray-700 px-3 py-2 rounded transition duration-300 ease-in-out transform hover:scale-105"
                  >
                    Admin Dashboard
                  </Link>
                )}
                {isOwner && (
                  <Link
                    to="/owner"
                    className="hover:bg-gray-700 px-3 py-2 rounded transition duration-300 ease-in-out transform hover:scale-105"
                  >
                    Your Villa
                  </Link>
                )}
                <Link
                  to="/wishlists"
                  className="hover:bg-gray-700 px-3 py-2 rounded transition duration-300 ease-in-out transform hover:scale-105"
                >
                  Wishlists
                </Link>
                {user ? (
                  <div className="relative">
                    <button
                      onClick={() => setShowProfileMenu(!showProfileMenu)}
                      className="flex items-center space-x-2 hover:bg-gray-700 px-3 py-2 rounded transition duration-300"
                    >
                      <AvatarCircle
                        src={user.photoURL}
                        name={user.displayName || user.email}
                        className="w-8 h-8"
                        textClassName="text-xs"
                      />
                      <ChevronDownIcon className="h-4 w-4" />
                    </button>
                    {showProfileMenu && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 text-gray-700 z-50">
                        <div className="px-4 py-2 border-b">
                          <p className="text-sm font-medium">Hi, {user.displayName}</p>
                        </div>
                        <button
                          onClick={goToProfile}
                          className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                        >
                          View Profile
                        </button>
                        <button
                          onClick={() => navigate("/bookings")}
                          className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                        >
                          Your Bookings
                        </button>
                        {isOwner && (
                          <Link
                            to="/owner"
                            className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                            onClick={() => setShowProfileMenu(false)}
                          >
                            Owner Dashboard
                          </Link>
                        )}
                        {isAdmin && (
                          <button
                            onClick={goToAdminDashboard}
                            className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                          >
                            Admin Dashboard
                          </button>
                        )}
                        <button
                          onClick={handleLogoutClick}
                          className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 text-red-600"
                        >
                          Logout
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={() => toggleLoginPopup(true)}
                    className="hover:bg-gray-700 px-3 py-2 rounded transition duration-300 ease-in-out transform hover:scale-105"
                  >
                    Login
                  </button>
                )}
              </nav>
            </div>
          </div>
        </header>
      )}

      {isMobile && showMobileMenu && (
        <div
          className={`fixed inset-0 z-50 ${isMenuClosing ? "mobile-menu-overlay-exit" : "mobile-menu-overlay-enter"}`}
        >
          <div
            className={`bg-white w-64 h-full shadow-lg overflow-y-auto ${isMenuClosing ? "animate-slideOutLeft" : "animate-slideInLeft"}`}
          >
            <div className="p-4 flex bg-black justify-between items-center border-b">
              <h2 className="text-xl text-white font-bold">Menu</h2>
              <XMarkIcon className="h-6 w-6 cursor-pointer text-white" onClick={toggleMobileMenu} />
            </div>
            <nav className="p-4">
              <Link
                to="/for-owner"
                className="block py-2 px-4 hover:bg-gray-100 rounded transition duration-300"
                onClick={toggleMobileMenu}
              >
                For House Owners
              </Link>
              <Link
                to="/wishlists"
                className="block py-2 px-4 hover:bg-gray-100 rounded transition duration-300"
                onClick={toggleMobileMenu}
              >
                Wishlists
              </Link>
              {isAdmin && (
                <Link
                  to="/admin"
                  className="block py-2 px-4 hover:bg-gray-100 rounded transition duration-300"
                  onClick={toggleMobileMenu}
                >
                  Admin Dashboard
                </Link>
              )}
              {user && (
                <button
                  onClick={goToProfile}
                  className="block w-full text-left py-2 px-4 hover:bg-gray-100 rounded transition duration-300"
                >
                  Profile
                </button>
              )}
              {user && isOwner && (
                <Link
                  to="/owner"
                  className="block py-2 px-4 hover:bg-gray-100 rounded transition duration-300"
                  onClick={toggleMobileMenu}
                >
                  Owner Dashboard
                </Link>
              )}
              {user ? (
                <button
                  onClick={handleLogoutClick}
                  className="w-full text-left py-2 px-4 hover:bg-gray-100 rounded transition duration-300"
                >
                  Logout
                </button>
              ) : (
                <button
                  onClick={() => {
                    toggleLoginPopup(true)
                    toggleMobileMenu()
                  }}
                  className="w-full text-left py-2 px-4 hover:bg-gray-100 rounded transition duration-300"
                >
                  Login
                </button>
              )}
            </nav>
          </div>
        </div>
      )}

      {/* Mobile transparent top bar */}
      {isMobile && (
        <div className="sticky top-2 z-40 bg-transparent">
          <div className="flex items-center justify-between px-6 h-14">
            <button
              onClick={() => {}}
              className="p-2 -ml-2 text-black"
              aria-label="Notifications"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                />
              </svg>
            </button>
            <span className="text-black text-[24px] font-bold">VillaKosh</span>
            {user ? (
              <button onClick={() => navigate('/profile')} className="p-1">
                <AvatarCircle
                  src={user.photoURL}
                  name={user.displayName || user.email}
                  className="w-9 h-9"
                  textClassName="text-xs"
                />
              </button>
            ) : (
              <button
                onClick={() => toggleLoginPopup(true)}
                className="text-black text-sm font-medium px-3 py-1 border border-black/30 rounded-full"
              >
                Login
              </button>
            )}
          </div>
        </div>
      )}

      {/* Header — desktop only */}
      {!isMobile && (
        <div className="sticky top-0 bg-[#EDEFEF] z-50 border-b border-gray-200">
          <div className="flex items-center justify-center px-4 py-3 md:container md:mx-auto md:px-16 relative">
            <button
              onClick={() => navigate(-1)}
              className="absolute left-4 flex items-center text-gray-800"
              aria-label="Back"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="text-2xl font-semibold text-center">Special Offers</h1>
          </div>
          <div className="container mx-auto px-4 pb-3 md:px-16">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search offers by location or name"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2.5 rounded-md bg-white border border-gray-300 text-sm text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-black/20 focus:border-black"
              />
            </div>
          </div>
        </div>
      )}
      {/* Ad Card Section */}
      <div className="container mx-auto px-4 mb-2 md:px-16">
        <AdCard
          adSlot="5283425303"
          className="w-full h-[100px] sm:h-[110px] md:h-[248px] lg:h-[248px] 
                    mx-auto rounded-xl overflow-hidden transition-all duration-300"
        />
      </div>

      <main className="pb-20 px-4 md:container md:mx-auto md:px-16 md:pb-8 pt-2">
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-black"></div>
          </div>
        ) : filteredOffers.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-sm">No active offers available at the moment.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredOffers.map((property) => {
              const discountedPrice = Math.round(property.rate * (1 - property.discount / 100))
              const validTill = new Date(property.offerValidTill).toLocaleDateString("en-IN", {
                day: "numeric",
                month: "short",
                year: "numeric",
              })

              return (
                <div
                  key={property._id}
                  onClick={() => navigate(`/villa/${property._id}`)}
                  className="relative rounded-2xl overflow-hidden cursor-pointer aspect-[16/10] md:aspect-[16/9]"
                >
                  {/* Background image */}
                  <OptimizedImage
                    src={property.thumbnail || property.images?.[0] || '/placeholder.svg'}
                    alt={property.name}
                    className="absolute inset-0 w-full h-full object-cover"
                    preset="cardSmall"
                  />
                  {/* Stronger dark overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/30" />

                  {/* Top-left: villa name + city with text shadow for readability */}
                  <div className="absolute top-3 left-3 right-14 text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.8)]">
                    <p className="text-sm font-bold leading-tight line-clamp-1" style={{textShadow: '0 1px 4px rgba(0,0,0,0.9)'}}>{property.name}</p>
                    <p className="text-[11px] text-white mt-0.5 line-clamp-1" style={{textShadow: '0 1px 3px rgba(0,0,0,0.9)'}}>
                      {property.mainCity}
                    </p>
                  </div>

                  {/* Discount badge top-right */}
                  <div className="absolute top-3 right-3 bg-white/90 text-black px-2.5 py-1 rounded-full text-[11px] font-bold">
                    {property.discount}% OFF
                  </div>

                  {/* Content overlay at bottom */}
                  <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                    <p className="text-sm font-bold leading-snug line-clamp-2">
                      {property.offerMessage || `Get an extra ${property.discount}% off on your booking!`}
                    </p>
                    <div className="flex items-baseline gap-2 mt-2">
                      <span className="text-sm text-white/70 line-through">₹{property.rate}</span>
                      <span className="text-xl font-bold">From ₹{discountedPrice}</span>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          setSelectedTnC(property.termsAndConditions)
                          setShowTnC(true)
                        }}
                        className="text-[11px] text-white/70 hover:text-white"
                      >
                        *T&amp;C Apply
                      </button>
                      <span className="text-[11px] text-white/70">
                        Valid till {validTill}
                      </span>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </main>

      {/* Floating Nearby Button */}
      {isMobile && (
        <button
          onClick={handleNearbySort}
          className={`fixed bottom-[5.5rem] right-4 z-20 py-2 px-4 rounded-full shadow-md flex items-center gap-2 ${
            isNearbySorted
              ? "bg-black text-white"
              : "bg-white text-gray-800 border border-gray-200"
          }`}
        >
          <Navigation className="h-4 w-4" />
          <span className="text-sm">Nearby</span>
        </button>
      )}

      {/* Footer for desktop */}
      {!isMobile && <Footer />}

      {isMobile && <MobileBottomNav user={user} activeTab="offers" />}

      <TermsModal isOpen={showTnC} onClose={() => setShowTnC(false)} terms={selectedTnC} />

      {showLoginPopup && <LoginPopup onClose={() => toggleLoginPopup(false)} />}
      {showLogoutConfirmation && (
        <LogoutConfirmationPopup onConfirm={handleLogoutConfirm} onCancel={handleLogoutCancel} />
      )}
      {(isBlurred || showMobileMenu) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-40"></div>
      )}
    </div>
  )
}

export default UserOffersPage

